import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Factory, Award, Users, Home, Briefcase, ArrowRight } from "lucide-react";

// Category images - grass uses public path for LCP preload discoverability
const grassImage = "/images/grass-40mm-natural-installed.jpg";
import deckingImage from "@/assets/products/wpc-decking-teak.jpg";
import claddingImage from "@/assets/products/wpc-cladding-natural.jpg";
import flooringImage from "@/assets/products/spc-honey-oak.jpg";
import hedgeImage from "@/assets/products/hedge-boxwood.jpg";
import acousticImage from "@/assets/products/acoustic-slat-oak.jpg";

const categories = [
  { name: "Artificial Grass", image: grassImage },
  { name: "WPC Decking", image: deckingImage },
  { name: "WPC Cladding", image: claddingImage },
  { name: "Hybrid Flooring", image: flooringImage },
  { name: "Hedges & Screens", image: hedgeImage },
  { name: "Acoustic Panels", image: acousticImage },
];

const trustBadges = [
  { icon: Factory, label: "Factory Direct" },
  { icon: Award, label: "Premium Quality" },
  { icon: Users, label: "Trusted by Trade & Homeowners" },
];

export function HeroSection() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % categories.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-[700px] lg:min-h-[800px] overflow-hidden">
      {/* Animated Background Images */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
        >
          <img
            src={categories[currentIndex].image}
            alt={categories[currentIndex].name}
            width={1920}
            height={1080}
            className="h-full w-full object-cover"
            style={{
              imageRendering: 'auto',
              WebkitBackfaceVisibility: 'hidden',
              backfaceVisibility: 'hidden',
            }}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </motion.div>
      </AnimatePresence>

      {/* Constant Dark Green Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/85 to-primary/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-primary/40" />

      {/* Content */}
      <div className="container relative z-10 flex min-h-[700px] flex-col justify-center py-16 lg:min-h-[800px] lg:py-24">
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 items-center">
          {/* Left Side - Main Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center lg:text-left"
          >
            {/* Animated Category Label */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.5 }}
                className="mb-4"
              >
                <span className="inline-flex items-center gap-2 rounded-full bg-accent/20 px-4 py-2 text-sm font-semibold text-accent backdrop-blur-sm border border-accent/30">
                  <span className="h-2 w-2 rounded-full bg-accent animate-pulse" />
                  {categories[currentIndex].name}
                </span>
              </motion.div>
            </AnimatePresence>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-6 text-4xl font-extrabold leading-tight tracking-tight text-primary-foreground md:text-5xl lg:text-6xl"
            >
              Premium Building & Landscaping Materials for Trade & Homeowners
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mb-8 text-lg text-primary-foreground/90 md:text-xl max-w-2xl mx-auto lg:mx-0"
            >
              Supplying quality materials across QLD, NSW, VIC & SA
            </motion.p>

            {/* Category Dots Indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="flex gap-2 justify-center lg:justify-start mb-8"
            >
              {categories.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? "w-8 bg-accent"
                      : "w-2 bg-primary-foreground/40 hover:bg-primary-foreground/60"
                  }`}
                  aria-label={`View ${categories[index].name}`}
                />
              ))}
            </motion.div>
          </motion.div>

          {/* Right Side - B2C/B2B CTA Blocks */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2"
          >
            {/* B2C Block - Homeowners */}
            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="group relative overflow-hidden rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-6 lg:p-8"
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-accent/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              />
              <div className="relative z-10">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-accent/20 group-hover:bg-accent/30 transition-colors duration-300">
                  <Home className="h-7 w-7 text-accent" />
                </div>
                <h2 className="mb-2 text-xl font-bold text-primary-foreground">
                  For Homeowners
                </h2>
                <p className="mb-4 text-sm text-primary-foreground/80">
                  Explore our premium range of building & landscaping materials
                </p>
                <Button
                  asChild
                  variant="hero"
                  size="lg"
                  className="w-full group/btn"
                >
                  <Link to="/shop">
                    Browse Products
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </div>
              {/* Pulse Animation */}
              <div className="absolute -bottom-10 -right-10 h-32 w-32 rounded-full bg-accent/10 blur-2xl group-hover:bg-accent/20 transition-colors duration-500" />
            </motion.div>

            {/* B2B Block - Trade */}
            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="group relative overflow-hidden rounded-2xl bg-primary-foreground/5 backdrop-blur-md border-2 border-primary-foreground/30 p-6 lg:p-8"
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-primary-foreground/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              />
              <div className="relative z-10">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary-foreground/10 group-hover:bg-primary-foreground/20 transition-colors duration-300">
                  <Briefcase className="h-7 w-7 text-primary-foreground" />
                </div>
                <h2 className="mb-2 text-xl font-bold text-primary-foreground">
                  For Trade & Business
                </h2>
                <p className="mb-4 text-sm text-primary-foreground/80">
                  Bulk pricing & project quotes for builders & landscapers
                </p>
                <Button
                  asChild
                  variant="heroOutline"
                  size="lg"
                  className="w-full group/btn"
                >
                  <Link to="/bulk-quote">
                    Get a Quote
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </div>
              {/* Outline Animation */}
              <motion.div
                className="absolute inset-0 rounded-2xl border-2 border-primary-foreground/0 group-hover:border-primary-foreground/40 transition-colors duration-500"
              />
            </motion.div>
          </motion.div>
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-12 lg:mt-16"
        >
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 lg:justify-start">
            {trustBadges.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1, duration: 0.4 }}
                className="flex items-center gap-3 text-primary-foreground/90"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/20 backdrop-blur-sm">
                  <item.icon className="h-5 w-5 text-accent" />
                </div>
                <span className="text-sm font-medium">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom Wave Transition */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-16 md:h-24"
          preserveAspectRatio="none"
        >
          <path
            d="M0,100 C360,20 1080,20 1440,100 L1440,100 L0,100 Z"
            className="fill-background"
          />
        </svg>
      </div>
    </section>
  );
}
